import { useEffect, useMemo, useRef, useState } from "react";

import CodeViewer from "@/components/CodeViewer";
import SkillMarkdown from "@/components/SkillMarkdown";
import type { SkillFile } from "@/lib/types";

const MIN_W = 180;
const MAX_W = 400;

type Node = { name: string; path: string; dir: boolean; children: Node[] };

function buildTree(files: SkillFile[]): Node[] {
  const root: Node = { name: "", path: "", dir: true, children: [] };
  for (const f of files) {
    const parts = f.path.split("/");
    let cur = root;
    parts.forEach((part, i) => {
      const isFile = i === parts.length - 1;
      let child = cur.children.find((c) => c.name === part);
      if (!child) {
        child = { name: part, path: parts.slice(0, i + 1).join("/"), dir: !isFile, children: [] };
        cur.children.push(child);
      }
      cur = child;
    });
  }
  const sort = (n: Node) => {
    n.children.sort((a, b) => (a.dir === b.dir ? a.name.localeCompare(b.name) : a.dir ? -1 : 1));
    n.children.forEach(sort);
  };
  sort(root);
  return root.children;
}

const FolderIcon = ({ open }: { open: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="shrink-0 text-text/45">
    {open ? (
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h6a2 2 0 0 1 2 2H8.5a2 2 0 0 0-1.9 1.4L4 19.5V7z M6.6 11.4A1 1 0 0 1 7.5 11H21a1 1 0 0 1 .95 1.3l-1.8 6A2 2 0 0 1 18.2 19H4l2.6-7.6z" />
    ) : (
      <path d="M3 5a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5z" />
    )}
  </svg>
);
const FileIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-text/40">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" />
  </svg>
);
const FilesToggleIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-text/50">
    <path d="M3 5a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5z" />
  </svg>
);

// True on phone-width viewports — drives the stacked file-picker layout below.
function useIsMobile() {
  const query = "(max-width: 767px)";
  const [mobile, setMobile] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMobile(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return mobile;
}
const Chevron = ({ open }: { open: boolean }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 text-text/40 transition-transform ${open ? "rotate-90" : ""}`}>
    <path d="M9 18l6-6-6-6" />
  </svg>
);

function Tree({ nodes, depth, selected, onSelect }: { nodes: Node[]; depth: number; selected: string; onSelect: (p: string) => void }) {
  return <ul>{nodes.map((n) => <TreeRow key={n.path} node={n} depth={depth} selected={selected} onSelect={onSelect} />)}</ul>;
}

function TreeRow({ node, depth, selected, onSelect }: { node: Node; depth: number; selected: string; onSelect: (p: string) => void }) {
  const [open, setOpen] = useState(true);
  const pad = 0.5 + depth * 0.85;

  if (node.dir) {
    return (
      <li>
        <button
          onClick={() => setOpen((o) => !o)}
          style={{ paddingLeft: `${pad}rem` }}
          className="flex w-full items-center gap-1.5 border-l-2 border-transparent py-1.5 pr-2 text-left font-display text-sm text-text/80 transition hover:bg-white/5"
        >
          <Chevron open={open} />
          <FolderIcon open={open} />
          <span className="truncate">{node.name}</span>
        </button>
        {open && <Tree nodes={node.children} depth={depth + 1} selected={selected} onSelect={onSelect} />}
      </li>
    );
  }

  const active = selected === node.path;
  return (
    <li>
      <button
        onClick={() => onSelect(node.path)}
        style={{ paddingLeft: `${pad}rem` }}
        className={`flex w-full items-center gap-1.5 border-l-2 py-1.5 pr-2 text-left font-display text-sm transition ${
          active ? "border-primary bg-white/[0.06] text-text" : "border-transparent text-text/70 hover:bg-white/5 hover:text-text"
        }`}
      >
        <span className="w-3 shrink-0" />
        <FileIcon />
        <span className="truncate">{node.name}</span>
      </button>
    </li>
  );
}

/**
 * Full-height file-system view of a skill version: a VS-Code-style file tree on the left
 * (fills the screen height, own scroll) and the right column (own scroll) showing `header`
 * (skill meta) on top, then the selected file's content, then `footer` (e.g. ratings).
 */
export default function FileBrowser({
  files,
  header,
  footer,
  initialPath,
  onSelect,
}: {
  files: SkillFile[];
  header?: React.ReactNode;
  footer?: React.ReactNode;
  /** Pre-select this file when it exists (e.g. from a `?path=` deep link). */
  initialPath?: string;
  /** Called whenever the user picks a file — lets the parent mirror it in the URL. */
  onSelect?: (path: string) => void;
}) {
  const tree = useMemo(() => buildTree(files), [files]);
  const [selected, setSelected] = useState("");
  // Seed the selection once files arrive (prefer the deep-linked path, else SKILL.md, else the
  // first file), and keep it valid if the file set changes.
  useEffect(() => {
    if (files.length === 0) return;
    setSelected((cur) => {
      if (cur && files.some((f) => f.path === cur)) return cur;
      if (initialPath && files.some((f) => f.path === initialPath)) return initialPath;
      return files.find((f) => f.path.endsWith("SKILL.md"))?.path ?? files[0].path;
    });
  }, [files, initialPath]);
  // Follow external (URL / back-button) path changes.
  useEffect(() => {
    if (initialPath && files.some((f) => f.path === initialPath)) setSelected(initialPath);
  }, [initialPath, files]);
  const select = (p: string) => { setSelected(p); onSelect?.(p); };
  const [width, setWidth] = useState(288); // sidebar width in px (resizable)
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [treeOpen, setTreeOpen] = useState(false); // mobile: file-tree drawer open?

  function startResize(e: React.MouseEvent) {
    e.preventDefault();
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    function move(ev: MouseEvent) {
      const left = containerRef.current?.getBoundingClientRect().left ?? 0;
      setWidth(Math.min(MAX_W, Math.max(MIN_W, ev.clientX - left)));
    }
    function up() {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
    }
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  }

  const file = files.find((f) => f.path === selected) ?? files[0];
  const isMd = (file?.path ?? "").toLowerCase().endsWith(".md");
  const isBinary = file?.content === "<binary file omitted>";

  const treeEl =
    files.length === 0 ? (
      <p className="px-3 py-1 text-sm text-text/40">No files.</p>
    ) : (
      // On mobile, picking a file also closes the drawer so the content is visible.
      <Tree nodes={tree} depth={0} selected={selected} onSelect={isMobile ? (p) => { select(p); setTreeOpen(false); } : select} />
    );

  const contentEl = (
    // Hide the scrollbar on phones (it still scrolls) — a visible bar in the pane reads as janky.
    <div className={`min-w-0 flex-1 overflow-y-auto${isMobile ? " no-scrollbar" : ""}`}>
      <div className={`max-w-none ${isMobile ? "px-4 py-5" : "px-8 py-7"}`}>
        {header}
        {file && (
          <div className={header ? "mt-6 border-t border-white/10 pt-6" : ""}>
            {isBinary ? (
              <p className="text-sm text-text/50">Binary file — not shown.</p>
            ) : isMd ? (
              <SkillMarkdown md={file.content} variant="prose" showFrontMatter />
            ) : (
              <CodeViewer code={file.content} path={file.path} plain />
            )}
          </div>
        )}
        {footer && <div className="mt-8 border-t border-white/10 pt-6">{footer}</div>}
      </div>
    </div>
  );

  // Phone layout: no room for a side-by-side tree + a drag handle is useless on touch. Stack the
  // tree into a full-width collapsible drawer above the content, which fills the width on its own.
  if (isMobile) {
    return (
      <div ref={containerRef} className="flex min-h-0 flex-1 flex-col border-t border-white/10">
        <button
          onClick={() => setTreeOpen((o) => !o)}
          aria-expanded={treeOpen}
          className="flex items-center gap-2 px-4 py-2.5 text-left"
          style={{ background: "var(--bg-subtle)", borderBottom: "1px solid var(--border)" }}
        >
          <FilesToggleIcon />
          <span className="min-w-0 flex-1 truncate font-mono text-xs text-text/70">
            {selected ? selected.split("/").pop() : "Browse files"}
          </span>
          <Chevron open={treeOpen} />
        </button>
        {treeOpen && (
          <div className="no-scrollbar max-h-[45vh] overflow-y-auto py-2" style={{ background: "var(--bg-subtle)", borderBottom: "1px solid var(--border)" }}>
            {treeEl}
          </div>
        )}
        {contentEl}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex min-h-0 flex-1 border-t border-white/10">
      <aside style={{ width, background: "var(--bg-subtle)" }} className="no-scrollbar shrink-0 overflow-y-auto py-2">
        <p className="mb-1 flex items-center gap-2 px-3 pb-1 font-mono text-[10px] uppercase tracking-[0.15em] text-text/45">
          <span className="h-px w-5 bg-primary" />
          Files
        </p>
        {treeEl}
      </aside>

      {/* Drag to resize */}
      <div
        onMouseDown={startResize}
        className="group relative w-px shrink-0 cursor-col-resize"
        style={{ background: "var(--border)" }}
        title="Drag to resize"
      >
        <div className="absolute inset-y-0 -left-1.5 -right-1.5 z-10" />
        <div className="absolute inset-y-0 left-0 w-px transition-colors group-hover:bg-primary/60" style={{ background: "var(--border)" }} />
      </div>

      {contentEl}
    </div>
  );
}
